/** English Entrepreneurship Support Translations */
export const entrepreneurship = {
  entrepreneurship: {
    pageTitle: "Entrepreneurship Support Dimension",
    weightInFII: "weight in YFI",
    heroSubtitle: "Empowering Young People to Create Sustainable Jobs & Build Wealth (18-40 years)",
    quote: "$1.7 trillion is the global financing gap for small and medium enterprises. Yet in Sub-Saharan Africa, 28% of young people actively pursue entrepreneurship—2.3x the global average. The gap is not ambition. It is access.",
    fiiWeightDistribution: "YFI Weight Distribution",
    mentalHealth: "Mental Health",
    workforceLabel: "Workforce",
    entrepreneurshipLabel: "Entrepreneurship",
    understandOpportunity: "Understand the opportunity",
    opportunityTitle: "The Entrepreneurship Opportunity in Numbers",
    opportunitySubtitle: "Critical statistics shaping global youth entrepreneurship",
    opportunity: {
      stat1Label: "global SME financing gap",
      stat2Label: "youth TEA rate in Sub-Saharan Africa (2.3x global average)",
      stat3Label: "global average entrepreneurship aspiration",
      stat4Label: "VC funding going to women entrepreneurs",
      stat5Label: "total VC funding going to women globally",
      stat6Label: "new startups launched annually",
      stat7Label: "startup failure rate within 5 years",
      stat8Label: "jobs created per 1% entrepreneurship increase",
    },
    severity: {
      barrier: "Barrier",
      opportunity: "Opportunity",
      gap: "Gap",
    },
    coreProblem: "The Core Problem",
    coreProblemDesc: "NOT a shortage of ambition. Entrepreneurship is the primary survival mechanism in economies with limited traditional employment. In Africa/ME, 52% of youth want to start businesses—it's not a luxury; it's the economy of the future. The real barriers: Capital gap ($1.7T globally), Skills gap (business planning, financial literacy), Trust gap (lenders don't trust young entrepreneurs), Infrastructure gap (regulatory burden, payment systems), and Gender gap (women face 2-3x additional barriers).",
    fiiTitle: "YFI Entrepreneurship Methodology",
    fiiSubtitle: "Entrepreneurship Score = 25% of Youth Futures Index",
    subWeight: "sub-weight",
    globalAverage: "Global average",
    highest: "Highest",
    lowest: "Lowest",
    fii: {
      easeOfBusiness: "Ease of Doing Business",
      easeOfBusinessDef: "Regulatory environment, startup friction, business registration",
      easeHighest: "Singapore, New Zealand (90+)",
      easeLowest: "Venezuela, Somalia (40-)",
      accessFinancing: "Access to Financing",
      accessFinancingDef: "Capital availability, credit access, VC, angel funding",
      accessHighest: "US, UK (80%+ population access)",
      accessLowest: "Sub-Saharan Africa (10-20%)",
      tea: "Early-Stage Entrepreneurship (TEA)",
      teaDef: "% of population actively starting/running new businesses",
      teaHighest: "Sub-Saharan Africa (28%)",
      teaLowest: "Europe (12%)",
      digitalInfra: "Digital Infrastructure",
      digitalInfraDef: "Internet, payment systems, e-commerce readiness",
      digitalHighest: "Nordic countries (95%)",
      digitalLowest: "Sub-Saharan Africa (20-30%)",
    },
    regionalTitle: "Regional Entrepreneurship Scores",
    regionalSubtitle: "YFI Entrepreneurship Dimension: Regional Performance",
    regions: {
      europe: "Europe",
      asiaPacific: "Asia and the Pacific",
      americas: "America",
      africaMe: "Africa and the Middle East",
    },
    status: {
      strong: "Strong",
      growing: "Growing",
      mixed: "Mixed",
      emerging: "Emerging",
    },
    regional: {
      europeDrivers: "Regulatory support, access to capital, incubators",
      europeOpportunity: "Risk-averse culture needs innovation acceleration",
      asiaPacificDrivers: "Unicorn ecosystem, government support, tech hubs",
      asiaPacificOpportunity: "Tier-2/3 cities need capital access",
      americasDrivers: "North America strong (80+), innovation culture",
      americasOpportunity: "Latin America needs capital democratization",
      africaMeDrivers: "Highest aspiration (52%), digital leapfrog",
      africaMeOpportunity: "Severe capital gap closure, MFI linkage",
    },
    strength: "Strength",
    challenge: "Opportunity",
    easeScore: "Ease Score",
    financingScore: "Financing",
    teaScore: "TEA Rate",
    digitalScore: "Digital",
    keyFinding: "Key Finding",
    keyFindingDesc: "Entrepreneurship scores vary by only 12 points (Europe 72 vs. Africa/ME 60) — the SMALLEST YFI gap. Yet entrepreneurship aspiration is HIGHEST in Africa/ME (52% vs. 35% global). This means capacity exists; only infrastructure is missing.",
    financingTitle: "The $1.7 Trillion Financing Gap",
    financingSubtitle: "Where the global SME financing gap originates and how JCI can help close it",
    gapByRegion: "Financing Gap by Region",
    trillion: "Trillion",
    totalGap: "Total Global SME Financing Gap",
    timeline: "Timeline",
    impact: "Impact",
    projectionTitle: "2024 vs 2040 Projection",
    financingGap: {
      subSaharan: "Sub-Saharan Africa",
      subSaharanGap: "$500B+",
      subSaharanCause: "No formal credit infrastructure, high risk perception",
      southAsia: "South Asia",
      southAsiaGap: "$400B+",
      southAsiaCause: "Regulatory barriers, informal economy dominance",
      southeastAsia: "Southeast Asia",
      southeastAsiaGap: "$200B+",
      southeastAsiaCause: "Regulatory inconsistency, VC concentration",
      latinAmerica: "Latin America",
      latinAmericaGap: "$300B+",
      latinAmericaCause: "Economic volatility, regulatory burden",
      mena: "Middle East/North Africa",
      menaGap: "$200B+",
      menaCause: "Limited capital markets, gender discrimination",
    },
    gapMeaning: "What 'Financing Gap' Really Means",
    gapTypes: {
      awareness: "Awareness Gap",
      awarenessDef: "Entrepreneurs don't know funding sources exist",
      access: "Access Gap",
      accessDef: "Capital concentrated geographically + demographically",
      skills: "Skills Gap",
      skillsDef: "Can't write proposals or financial projections",
      trust: "Trust Gap",
      trustDef: "Lenders don't trust young entrepreneurs",
      collateral: "Collateral Gap",
      collateralDef: "Poor youth have no assets to pledge",
      gender: "Gender Gap",
      genderDef: "Women face 2-3x higher institutional barriers",
    },
    landscapeTitle: "Global Entrepreneurship Landscape",
    landscapeSubtitle: "Entrepreneurship metrics by region (GEM 2024-25)",
    teaRate: "TEA Rate",
    businessTypes: "Business Types",
    financing: "Financing",
    successRate: "Success Rate",
    landscape: {
      subSaharanTitle: "Sub-Saharan Africa",
      subSaharanTea: "28% TEA (HIGHEST globally)",
      subSaharanTypes: "70% micro, 25% SMEs, 5% scalable",
      subSaharanFinancing: "80% self-funded/family; 5% formal",
      subSaharanSuccess: "40% survival rate",
      southeastAsiaTitle: "Southeast Asia",
      southeastAsiaTea: "20% TEA (strong, growing)",
      southeastAsiaTypes: "40% digital, 35% retail, 25% manufacturing",
      southeastAsiaFinancing: "50% bootstrap, 30% angel/VC",
      southeastAsiaSuccess: "55% survival rate",
      latinAmericaTitle: "Latin America",
      latinAmericaTea: "18% TEA (growing)",
      latinAmericaTypes: "60% micro, 30% SMEs, 10% scalable",
      latinAmericaFinancing: "70% self-funded, 20% family",
      latinAmericaSuccess: "45% survival rate",
      europeTitle: "Europe",
      europeTea: "12% TEA (mature, stable)",
      europeTypes: "20% micro, 30% SMEs, 50% scalable",
      europeFinancing: "60% institutional, 40% self-funded",
      europeSuccess: "65% survival rate",
      northAmericaTitle: "North America",
      northAmericaTea: "15% TEA (innovation-led)",
      northAmericaTypes: "10% micro, 20% SMEs, 70% scalable",
      northAmericaFinancing: "50% VC/angel, 30% institutional",
      northAmericaSuccess: "70% survival rate",
    },
    deepDiveTitle: "Regional Deep Dives",
    deepDiveSubtitle: "In-depth entrepreneurship analysis by JCI region",
    strengths: "Strengths",
    challenges: "Challenges",
    jciOpportunity: "JCI Opportunity",
    deepDive: {
      europe: {
        strength1: "World-class infrastructure (regulatory clarity, digital systems)",
        strength2: "Access to capital: €80B+ invested 2024",
        strength3: "Mature startup ecosystem: incubators, accelerators",
        challenge1: "Risk aversion: failure stigmatized",
        challenge2: "Brain drain to US/Asia",
        challenge3: "Capital concentration in London, Berlin, Paris",
        opportunity: "Focus on innovation acceleration, deep tech startups",
      },
      asiaPacific: {
        strength1: "Unicorn ecosystem: China 200+, India 80+",
        strength2: "Government acceleration: billions invested",
        strength3: "Demographic dividend: 35% under 25",
        challenge1: "Extreme inequality: wealth in tier-1 cities",
        challenge2: "Regulatory volatility",
        challenge3: "Gender barriers: cultural + institutional",
        opportunity: "Tier-2/3 startup initiative, women in tech programs",
      },
      americas: {
        strength1: "Largest VC market ($150B+ invested 2024)",
        strength2: "Innovation culture: Silicon Valley, NYC, Toronto",
        strength3: "Diverse markets: tech, biotech, fintech",
        challenge1: "14-point North-South divide",
        challenge2: "Latin America regulatory burden",
        challenge3: "Only 2% of LAC startups get VC",
        opportunity: "Bridge North-South mentor linkages, LAC capacity",
      },
      africaMe: {
        strength1: "Highest aspiration globally: 52% want business",
        strength2: "Young population: 60% under 25",
        strength3: "Digital leapfrog: mobile-first (95%+ have phones)",
        challenge1: "Financing gap MASSIVE: 80% self-funded",
        challenge2: "Skill gaps: business planning, accounting",
        challenge3: "Girls systematically excluded",
        opportunity: "$10T opportunity: reach 5% of 1.2B youth → 6M jobs",
      },
    },
    caseStudiesTitle: "Success Stories",
    caseStudiesSubtitle: "Real CYE winners demonstrating JCI's entrepreneurship impact",
    patternTitle: "CYE Success Pattern",
    pattern: {
      real: "Solves real problems (waste, food, employment)",
      female: "Has female participation (higher success, community benefit)",
      market: "Links to market opportunity (not just prototype)",
      mentorship: "Provides mentorship + capital (not just competition)",
    },
    cases: {
      japjapCountry: "Hong Kong",
      japjapDesc: "Food waste → protein + compost using AI-IoT technology",
      japjapFounder: "Female founder, immigrant background",
      japjapMetric: "tonnes food waste processed",
      integraCountry: "Panama",
      integraDesc: "Food supply chain efficiency for Latin America",
      integraFounder: "JCI CYE winner with mentorship",
      integraMetric: "tons CO2 reduced annually",
      grinoveCountry: "Turkey",
      grinoveDesc: "Agricultural waste → compostable biomaterials",
      grinoveFounder: "Female scientist-turned-entrepreneur",
      grinoveMetric: "Prototypes ready",
      grinoveMetricLabel: "European expansion",
    },
    actionsTitle: "Recommended Actions for JCI",
    actionsSubtitle: "Strategic priorities to close the entrepreneurship gap",
    actions: {
      action1Title: "Scale CYE 10x",
      action1Desc: "Expand from 108 to 1,000 entrepreneurs/year, reaching 5,000 by 2030",
      action1Effort: "18-24 months",
      action1Impact: "10x reach",
      action2Title: "Women's Entrepreneurship Fund",
      action2Desc: "Create $100M fund for female founders with microfinance + mentorship",
      action2Effort: "18 months",
      action2Impact: "50K women by 2030",
      action3Title: "MFI Partnerships",
      action3Desc: "Formalize partnerships with 50 microfinance institutions",
      action3Effort: "12 months",
      action3Impact: "500K credit access",
      action4Title: "JCI Incubation Hubs",
      action4Desc: "Build 30 incubation hubs across Africa/Asia/LAC",
      action4Effort: "24 months",
      action4Impact: "10K accelerated",
      action5Title: "Startup Marketplace",
      action5Desc: "Create digital platform connecting entrepreneurs with suppliers, customers, mentors",
      action5Effort: "9-12 months",
      action5Impact: "100K+ connected",
      action6Title: "JIB Regional Networks",
      action6Desc: "Establish mentorship networks linking 5,000+ mentors with founders",
      action6Effort: "12 months",
      action6Impact: "5K+ mentors",
    },
    ctaTitle: "Entrepreneurship Creates Jobs",
    ctaDescription: "When traditional employment is scarce, entrepreneurship becomes the PRIMARY pathway to opportunity. 1 entrepreneur creates 3-5 jobs. If JCI scales 10x by 2030: 5M jobs created, $500B+ in business value globally.",
    ctaExplore: "Explore YFI",
    ctaVision: "Vision 2040",
    sourcesTitle: "Data Sources & Attribution",
  },
} as const;

export type EntrepreneurshipTranslations = typeof entrepreneurship;
